import { Button, Loading, Modal, Typography, useAlert } from "matsuri-ui"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { css } from "@emotion/react"
import { CompletedView } from "./CompletedView"
import { CompressingPreview, ReportFormView } from "./ReportFormView"
import { ConfirmView } from "./ConfirmView"
import { DisposalView } from "./DisposalView"
import { EntryView } from "./EntryView"
import { QuestionView } from "./QuestionView"
import {
    DisposalExamples,
    LostItemMaxImageCount,
    LostItemMinImageCount,
    ValuableExamples
} from "./constants"
import { CenteredBody, Main } from "./styled"
import { takeWithinPhotoLimit } from "./utils"
import { BackToDetailConfirmDialog } from "@/components/Dialog/BackToDetailConfirmDialog"
import { assertIsDefined } from "@/utils/assetIsDefined"
import {
    genCleaningInfoPagePath,
    genCleaningReportPagePath
} from "@/routes/protected"
import { nextPathAfterLostItem } from "@/pages/CleaningReportPage/routes/nextPath"
import { useCleaning } from "@/hooks/useCleaning"
import { useImmediatePhotoUpload } from "@/hooks/useImmediatePhotoUpload"
import {
    LostItemCategory,
    LostItemReport,
    useLostItemReport
} from "@/hooks/useLostItemReport"
import { useMobileLayoutProps } from "@/hooks/useMobileLayoutProps"
import { useNavigateBackToDetail } from "@/hooks/useNavigateBackToDetail"
import { useTranslation } from "@/hooks/useTranslation"
import { imageCompressToWebp } from "@/utils/image"

// 入口（忘れ物はあったか）→ Q1（廃棄してよいか）→ Q2（貴重品か）→ 入力 → 確認 → 完了。
// Q1 が「当てはまる」なら廃棄指示のみで、報告レコードは作らない。
// 完了後は入口に戻り、もう一件報告するかゴミストッカー確認へ進むかを選ばせる。
type Step =
    "entry" | "q1" | "q2" | "disposal" | "form" | "confirm" | "completed"

// 完了報告フローの1ステップとして開くか、清掃詳細から単独で開くか。
// 単独時は「次へ」が完了報告の続きではなく清掃詳細に戻るため、離脱先と文言を変える。
type Mode = "report-flow" | "standalone"

export const LostItemPage = ({ mode = "report-flow" }: { mode?: Mode }) => {
    const { t } = useTranslation()
    const { cleaningId } = useParams<"cleaningId">()
    assertIsDefined(cleaningId)
    const isStandalone = mode === "standalone"

    const navigate = useNavigate()
    const { token } = useAuth()
    const { throwAlert } = useAlert()

    const { data: cleaning } = useCleaning(token, cleaningId)
    const listingName = cleaning?.cleaningInfo.listingName

    const {
        isTarget,
        reports,
        isLoading,
        error,
        uploadImage,
        createReport,
        deleteReport
    } = useLostItemReport(cleaningId)

    const {
        photos,
        addPhoto,
        removePhoto,
        retryPhoto,
        getReadyUploadIds,
        getHasError,
        waitAllSettled,
        reset,
        isUploading,
        hasError
    } = useImmediatePhotoUpload({ put: uploadImage })

    const [step, setStep] = useState<Step>("entry")
    // 廃棄物として処理したか。廃棄は報告レコードを作らないため、入口画面が
    // 「1周した」ことを知る手段がこれしかない（リロードすると失われる）。
    const [hasDisposed, setHasDisposed] = useState(false)
    const [category, setCategory] = useState<LostItemCategory>("general")
    const [itemDescription, setItemDescription] = useState("")
    const [storagePlace, setStoragePlace] = useState("")
    const [note, setNote] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [createdReport, setCreatedReport] = useState<LostItemReport>()
    const [deleteTarget, setDeleteTarget] = useState<LostItemReport>()
    const [deletingId, setDeletingId] = useState<string>()
    const [compressingPreviews, setCompressingPreviews] = useState<
        CompressingPreview[]
    >([])
    const compressingPreviewsRef = useRef<CompressingPreview[]>([])
    const previewCounterRef = useRef(0)
    // 入力をリセットするたびに進める。進行中の圧縮の結果を捨てる判定に使う。
    const formGenerationRef = useRef(0)

    useEffect(() => {
        compressingPreviewsRef.current = compressingPreviews
    }, [compressingPreviews])

    // unmount 時に圧縮中プレビューの objectURL を revoke する
    useEffect(
        () => () => {
            compressingPreviewsRef.current.forEach(p =>
                URL.revokeObjectURL(p.objectURL)
            )
        },
        []
    )

    const {
        confirmBackOpen,
        openConfirmBack,
        closeConfirmBack,
        goBackToDetail
    } = useNavigateBackToDetail("cleaning", cleaningId)

    // ステップを跨いでヘッダー/フッターの高さを測り直さないよう、ここで1度だけ呼ぶ
    const layout = useMobileLayoutProps()

    // 完了報告フローではゴミストッカー確認へ、単独で開いた時は清掃詳細へ戻す。
    const goNext = useCallback(() => {
        if (isStandalone) {
            navigate(genCleaningInfoPagePath(cleaningId, "info"))
            return
        }
        navigate(genCleaningReportPagePath(cleaningId, nextPathAfterLostItem()))
    }, [navigate, cleaningId, isStandalone])

    // 入力内容と撮影済み写真を捨てる。次の報告に前の入力が残らないようにする。
    const resetForm = useCallback(() => {
        setItemDescription("")
        setStoragePlace("")
        setNote("")
        // 圧縮中のものは reset() では止められないので、世代を進めて結果を捨てる
        formGenerationRef.current++
        setCompressingPreviews([])
        reset()
    }, [reset])

    const startQuestion = useCallback(() => {
        resetForm()
        setStep("q1")
    }, [resetForm])

    const backToEntry = useCallback(() => {
        resetForm()
        setStep("entry")
    }, [resetForm])

    // 廃棄して入口に戻る。廃棄済みを記録しないと入口が初回表示のままになり、
    // 「あった・報告する」→ Q1 →「廃棄する」→ 入口 とループして抜けられない。
    const handleDisposed = useCallback(() => {
        setHasDisposed(true)
        backToEntry()
    }, [backToEntry])

    const handleFilesSelected = useCallback(
        async (files: File[]) => {
            const { accepted, rejectedCount } = takeWithinPhotoLimit(
                files,
                photos.length + compressingPreviews.length
            )
            if (rejectedCount > 0) {
                throwAlert(new Error("too many photos"), {
                    errorMessage: {
                        happend: t("写真は3枚までです"),
                        action: t("追加できる分のみ取り込みました。")
                    },
                    ignoreSuccess: true
                })
            }
            if (accepted.length === 0) return

            // 圧縮の完了は非同期なので、待っている間に画面を戻られたら結果を捨てる
            // （前の物品の写真が次の報告に混ざるのを防ぐ）
            const generation = formGenerationRef.current

            await Promise.all(
                accepted.map(async file => {
                    const previewId = `preview-${previewCounterRef.current++}`
                    const previewURL = URL.createObjectURL(file)
                    setCompressingPreviews(prev =>
                        generation === formGenerationRef.current
                            ? [
                                  ...prev,
                                  { id: previewId, objectURL: previewURL }
                              ]
                            : prev
                    )
                    try {
                        const compressed = await imageCompressToWebp(file, {
                            quality: 0.85,
                            maxWidth: 1500,
                            maxHeight: 1500
                        })
                        if (generation === formGenerationRef.current) {
                            addPhoto(compressed)
                        }
                    } finally {
                        setCompressingPreviews(prev =>
                            prev.filter(p => p.id !== previewId)
                        )
                        URL.revokeObjectURL(previewURL)
                    }
                })
            )
        },
        [addPhoto, photos.length, compressingPreviews.length, throwAlert, t]
    )

    // 写真は1〜3枚必須（API のバリデーションと同じ）。アップロード中・失敗中は先に進ませない。
    const canConfirm = useMemo(
        () =>
            itemDescription.trim().length > 0 &&
            storagePlace.trim().length > 0 &&
            photos.length >= LostItemMinImageCount &&
            photos.length <= LostItemMaxImageCount &&
            compressingPreviews.length === 0 &&
            !isUploading &&
            !hasError,
        [
            itemDescription,
            storagePlace,
            photos.length,
            compressingPreviews.length,
            isUploading,
            hasError
        ]
    )

    const handleSubmit = useCallback(async () => {
        setSubmitting(true)
        try {
            // 進行中の PUT がすべて putDone / error に落ち着くまで待つ
            await waitAllSettled()
            if (getHasError()) {
                throw new Error("upload error")
            }
            const imageUploadIds = getReadyUploadIds()
            if (imageUploadIds.length === 0) {
                throw new Error("no images")
            }

            const report = await createReport({
                category,
                itemDescription: itemDescription.trim(),
                storagePlace: storagePlace.trim(),
                note: note.trim() || null,
                imageUploadIds
            })

            // 1件ずつ永続化し、完了画面で次の操作（もう一件 / 次へ）を選ばせる
            setCreatedReport(report)
            resetForm()
            setStep("completed")
        } catch (e) {
            throwAlert(e instanceof Error ? e : new Error(String(e)), {
                errorMessage: {
                    happend: t("忘れ物の報告に失敗しました"),
                    action: t(
                        "ネットワーク接続を確認してから、もう一度お試しください。問題が続く場合は、管理者にお問い合わせください。"
                    )
                },
                ignoreSuccess: true
            })
        } finally {
            setSubmitting(false)
        }
    }, [
        waitAllSettled,
        getHasError,
        getReadyUploadIds,
        createReport,
        category,
        itemDescription,
        storagePlace,
        note,
        resetForm,
        throwAlert,
        t
    ])

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteTarget) return
        setDeletingId(deleteTarget.id)
        try {
            await deleteReport(deleteTarget.id)
            setDeleteTarget(undefined)
        } catch (e) {
            throwAlert(e instanceof Error ? e : new Error(String(e)), {
                errorMessage: {
                    happend: t("報告の取り消しに失敗しました"),
                    action: t(
                        "ネットワーク接続を確認してから、もう一度お試しください。問題が続く場合は、管理者にお問い合わせください。"
                    )
                },
                ignoreSuccess: true
            })
        } finally {
            setDeletingId(undefined)
        }
    }, [deleteTarget, deleteReport, throwAlert, t])

    const backToDetailDialog = (
        <BackToDetailConfirmDialog
            type="cleaning"
            open={confirmBackOpen}
            onClose={closeConfirmBack}
            onConfirm={goBackToDetail}
        />
    )

    // 対象判定の取得に失敗した場合。報告フローを止めないようスキップ導線を出す。
    if (error) {
        return (
            <Main style={layout.rootProps.style}>
                <CenteredBody>
                    <Typography color="error">
                        {t(
                            "忘れ物報告の情報取得に失敗しました。電波の良い場所で開き直すか、スキップして報告を続けてください。"
                        )}
                    </Typography>
                    <Button variant="filled" color="primary" onClick={goNext}>
                        {t("スキップして次へ進む")}
                    </Button>
                </CenteredBody>
                {backToDetailDialog}
            </Main>
        )
    }

    if (isLoading || isTarget === undefined) {
        return (
            <Main style={layout.rootProps.style}>
                <CenteredBody>
                    <Loading
                        css={css`
                            font-size: 28px;
                        `}
                    />
                </CenteredBody>
            </Main>
        )
    }

    // 対象エリア外の清掃はこの画面をスキップする。
    // 完了報告フローではゴミストッカー確認へ、単独で開いた時は清掃詳細へ戻す。
    if (!isTarget) {
        return (
            <Navigate
                to={
                    isStandalone
                        ? genCleaningInfoPagePath(cleaningId, "info")
                        : genCleaningReportPagePath(
                              cleaningId,
                              nextPathAfterLostItem()
                          )
                }
                replace
            />
        )
    }

    if (step === "q1") {
        return (
            <>
                <QuestionView
                    step={1}
                    lead={[
                        t("当てはまる場合は廃棄してください。"),
                        t("報告は不要です。")
                    ]}
                    examplesTitle={t("廃棄して良いもの")}
                    examples={DisposalExamples}
                    yesLabel={t("当てはまる → 廃棄する")}
                    noLabel={t("当てはまらない")}
                    listingName={listingName}
                    layout={layout}
                    onYes={() => setStep("disposal")}
                    onNo={() => setStep("q2")}
                    onPrev={backToEntry}
                    onClose={openConfirmBack}
                />
                {backToDetailDialog}
            </>
        )
    }

    if (step === "q2") {
        return (
            <>
                <QuestionView
                    step={2}
                    lead={[
                        t("当てはまる場合は貴重品として報告します。"),
                        t("当てはまらない場合は一般品として報告します。")
                    ]}
                    examplesTitle={t("貴重品")}
                    examples={ValuableExamples}
                    yesLabel={t("当てはまる → 貴重品")}
                    noLabel={t("当てはまらない → 一般品")}
                    listingName={listingName}
                    layout={layout}
                    onYes={() => {
                        setCategory("valuable")
                        setStep("form")
                    }}
                    onNo={() => {
                        setCategory("general")
                        setStep("form")
                    }}
                    onPrev={() => setStep("q1")}
                    onClose={openConfirmBack}
                />
                {backToDetailDialog}
            </>
        )
    }

    if (step === "disposal") {
        return (
            <>
                <DisposalView
                    layout={layout}
                    onBack={() => setStep("q1")}
                    onDone={handleDisposed}
                    onClose={openConfirmBack}
                />
                {backToDetailDialog}
            </>
        )
    }

    if (step === "form") {
        return (
            <>
                <ReportFormView
                    category={category}
                    itemDescription={itemDescription}
                    storagePlace={storagePlace}
                    note={note}
                    photos={photos}
                    compressingPreviews={compressingPreviews}
                    canSubmit={canConfirm}
                    layout={layout}
                    onChangeItemDescription={setItemDescription}
                    onChangeStoragePlace={setStoragePlace}
                    onChangeNote={setNote}
                    onFilesSelected={handleFilesSelected}
                    onRemovePhoto={removePhoto}
                    onRetryPhoto={retryPhoto}
                    onConfirm={() => setStep("confirm")}
                    onBack={() => setStep("q2")}
                    onClose={openConfirmBack}
                />
                {backToDetailDialog}
            </>
        )
    }

    if (step === "confirm") {
        return (
            <>
                <ConfirmView
                    category={category}
                    itemDescription={itemDescription}
                    storagePlace={storagePlace}
                    note={note}
                    photos={photos}
                    submitting={submitting}
                    layout={layout}
                    onSubmit={handleSubmit}
                    onBack={() => setStep("form")}
                    onClose={openConfirmBack}
                />
                {backToDetailDialog}
            </>
        )
    }

    if (step === "completed" && createdReport) {
        return (
            <>
                <CompletedView
                    report={createdReport}
                    listingName={listingName}
                    layout={layout}
                    standalone={isStandalone}
                    onAddAnother={startQuestion}
                    onNext={goNext}
                />
                {backToDetailDialog}
            </>
        )
    }

    return (
        <>
            <EntryView
                reports={reports}
                hasDisposed={hasDisposed}
                listingName={listingName}
                deletingId={deletingId}
                layout={layout}
                standalone={isStandalone}
                onReport={startQuestion}
                onDelete={setDeleteTarget}
                onNext={goNext}
                onClose={openConfirmBack}
            />
            <Modal
                width={"calc(100% - 32px)"}
                backdrop
                open={!!deleteTarget}
                body={
                    <Typography>
                        {t("この報告を取り消しますか？")}
                        {deleteTarget && `（${deleteTarget.itemDescription}）`}
                    </Typography>
                }
                footer={
                    <>
                        <Button
                            variant="filled"
                            color="error"
                            fullWidth
                            disabled={!!deletingId}
                            onClick={handleDeleteConfirm}
                        >
                            {t("取り消す")}
                        </Button>
                        <Button
                            variant="outlined"
                            fullWidth
                            disabled={!!deletingId}
                            onClick={() => setDeleteTarget(undefined)}
                            css={css`
                                margin-top: 8px;
                            `}
                        >
                            {t("キャンセル")}
                        </Button>
                    </>
                }
            />
            {backToDetailDialog}
        </>
    )
}
